import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertTriangle, CalendarClock, LayoutDashboard, Package, Wallet } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { formatLbp, formatUsd } from '@/lib/money'
import type { Dashboard } from '@/lib/reports'
import { EmptyState } from '@/components/ui/primitives'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { t } from '@/i18n'

export function DashboardPage() {
  const canView = useSession((state) => state.can)(Permission.ViewDashboard)

  const dashboard = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => apiClient.get<Dashboard>('/api/reports/dashboard'),
    enabled: canView,
    // Deliberately no staleTime of its own. Today's takings and the cash position are exactly
    // the figures an owner checks to decide something, and this is the one screen where a
    // minute-old number reads as the current one.
  })

  if (!canView) {
    return (
      <div className="pb-page px-4 py-6">
        <EmptyState
          icon={LayoutDashboard}
          title={t('No access to the dashboard')}
          description={t('Ask an administrator for the reports permission.')}
        />
      </div>
    )
  }

  const data = dashboard.data

  return (
    <div className="pb-page mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
      <header className="mb-5">
        <h1 className="text-xl font-semibold">{t('Today')}</h1>
        <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
          {t('Where the shop stands right now.')}
        </p>
      </header>

      {!data ? (
        <p className="text-sm text-[var(--text-secondary)]">{t('Loading…')}</p>
      ) : (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-3">
            <Tile
              label={t('Sales today')}
              value={formatUsd(data.todaySalesUsd)}
              hint={`${data.todaySaleCount} ${data.todaySaleCount === 1 ? 'sale' : 'sales'}`}
            />
            <Tile
              label={t('Profit today')}
              value={formatUsd(data.todayGrossProfitUsd)}
              hint={t('after cost of goods')}
            />
            <Tile
              label={t('Owed to you')}
              value={formatUsd(data.receivablesUsd)}
              hint={t('on customer accounts')}
            />
          </div>

          {/* Cash sits in two piles and two currencies. Adding them up would hide which. */}
          <section className="surface-raised mb-4 rounded-[var(--radius-card)] border p-4">
            <p className="mb-3 flex items-center gap-2 text-sm font-medium">
              <Wallet className="h-4 w-4" />
              {t('Cash position')}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-[var(--text-secondary)]">{t('In open tills')}</p>
                <p className="tabular mt-0.5 text-lg font-semibold">{formatUsd(data.drawerUsd)}</p>
                <p className="tabular text-sm text-[var(--text-secondary)]">
                  {formatLbp(data.drawerLbp)}
                </p>
              </div>
              <div>
                <p className="text-sm text-[var(--text-secondary)]">{t('In the safe')}</p>
                <p className="tabular mt-0.5 text-lg font-semibold">{formatUsd(data.safeUsd)}</p>
                <p className="tabular text-sm text-[var(--text-secondary)]">
                  {formatLbp(data.safeLbp)}
                </p>
              </div>
            </div>
          </section>

          {(data.lowStockCount > 0 || data.expiringCount > 0) && (
            <div className="mb-4 grid gap-3 sm:grid-cols-2">
              {data.lowStockCount > 0 && (
                <Alert
                  icon={Package}
                  to="/inventory"
                  title={`${data.lowStockCount} ${data.lowStockCount === 1 ? 'item' : 'items'} running low`}
                  hint={t('At or below their reorder level')}
                />
              )}
              {data.expiringCount > 0 && (
                <Alert
                  icon={CalendarClock}
                  to="/inventory"
                  title={`${data.expiringCount} ${data.expiringCount === 1 ? 'batch' : 'batches'} expiring`}
                  hint={t('Sell, discount or write off')}
                />
              )}
            </div>
          )}

          <Trend days={data.trend} />

          {data.topProducts.length > 0 && (
            <section className="surface-raised rounded-[var(--radius-card)] border p-4">
              <p className="mb-2 text-sm font-medium">{t('Best sellers, last 30 days')}</p>
              <ul className="flex flex-col gap-1 text-sm">
                {data.topProducts.map((product) => (
                  <li key={product.productId} className="flex justify-between gap-3">
                    <span className="min-w-0 truncate">{product.name}</span>
                    <span className="tabular shrink-0">{formatUsd(product.netUsd)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  )
}

function Tile({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="surface-raised rounded-[var(--radius-card)] border p-4">
      <p className="text-sm text-[var(--text-secondary)]">{label}</p>
      <p className="tabular mt-1 text-2xl font-semibold">{value}</p>
      <p className="text-xs text-[var(--text-secondary)]">{hint}</p>
    </div>
  )
}

function Alert({
  icon: Icon,
  to,
  title,
  hint,
}: {
  icon: typeof Package
  to: string
  title: string
  hint: string
}) {
  return (
    <Link
      to={to}
      className="surface-raised flex items-center gap-3 rounded-[var(--radius-card)] border border-warn-500/40 p-4 transition-colors hover:border-warn-500"
    >
      <AlertTriangle className="h-5 w-5 shrink-0 text-warn-500" />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{title}</p>
        <p className="truncate text-xs text-[var(--text-secondary)]">{hint}</p>
      </div>
      <Icon className="ml-auto h-4 w-4 shrink-0 text-[var(--text-secondary)]" />
    </Link>
  )
}

/**
 * Thirty days of takings as bars.
 *
 * Drawn with plain divs rather than a charting library: it is one series of thirty numbers, and
 * a chart package would be more bytes over a shop's connection than the whole rest of the screen.
 */
function Trend({ days }: { days: Dashboard['trend'] }) {
  if (days.length === 0) {
    return null
  }

  const peak = Math.max(...days.map((day) => day.netUsd), 1)

  return (
    <section className="surface-raised mb-4 rounded-[var(--radius-card)] border p-4">
      <p className="mb-3 text-sm font-medium">{t('Last 30 days')}</p>
      <div className="flex h-24 items-end gap-1">
        {days.map((day) => (
          <div
            key={day.date}
            className="flex-1 rounded-t bg-[var(--accent)]"
            style={{ height: `${Math.max(2, (day.netUsd / peak) * 100)}%` }}
            title={`${day.date} · ${formatUsd(day.netUsd)}`}
          />
        ))}
      </div>
      <div className="mt-2 flex justify-between text-xs text-[var(--text-secondary)]">
        <span>{days[0]?.date}</span>
        <span className="tabular">peak {formatUsd(peak)}</span>
        <span>{days[days.length - 1]?.date}</span>
      </div>
    </section>
  )
}
